import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationService } from '@app/core/services/validation.service';
import { HealthProfessionalService } from '@app/modules/health-professional/services/health-professional.service';
import { PatientsService } from '@app/modules/patients/services/patients.service';
import { DateUtils } from '@app/shared/data/DateUtils';
import { HealthProfessional, Patient } from '@app/shared/models';
import { ProfessionalType } from '@app/shared/models/professional-type.enum';
import { AppToastService } from '@app/shared/services/app-toast.service';
import { BigboostService } from '@app/shared/services/bigboost.service';
import { markFormGroup } from '@app/utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TypePatientEnum } from '../../enum/type-patient';
import { nativeAsync } from '@app/shared/decorators/nativeAsync';

@Component({
  selector: 'app-modal-search',
  templateUrl: './modal-search.component.html',
  styleUrls: ['./modal-search.component.scss']
})
export class ModalSearchComponent implements OnInit {
  @Input() user: any;
  @Input() prescriber: HealthProfessional;
  @Input() addPatient = false;
  @Input() cpf: string;
  @Output() searchEvent = new EventEmitter();
  @Output() openModalAddPatientEvent = new EventEmitter<any>();
  @Output() openModalSelectPetEvent = new EventEmitter<any>();
  @Output() updateListPatientEvent = new EventEmitter<any>();
  @Output() loadingSpinnerEvent = new EventEmitter<boolean>();

  form: FormGroup;
  loading = false;
  showPetsList: boolean;

  enumTypePatient = TypePatientEnum;
  typePatientSelect: TypePatientEnum;

  alertOptions = {
    nzDuration: 5000
  };

  constructor(
    private fb: FormBuilder,
    public modalRef: BsModalRef,
    private patientsService: PatientsService,
    private healthProfessionalService: HealthProfessionalService,
    private notification: AppToastService,
    private bigboostService: BigboostService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      cpf: [
        this.cpf,
        [
          Validators.required,
          Validators.minLength(11),
          Validators.maxLength(11),
          ValidationService.cpfValidator
        ]
      ],
      dateOfBirth: [],
      noCpf: [this.isVeterinarian]
    });

    this.form.get('noCpf').valueChanges.subscribe(value => {
      if (value) {
        this.form.get('dateOfBirth').clearValidators();
      } else {
        this.form.get('dateOfBirth').setValidators([Validators.required]);
      }
    });
  }

  get noCpf() {
    return this.form.value.noCpf;
  }

  get isVeterinarian(): boolean {
    return this.prescriber && this.prescriber.professionalType === ProfessionalType.VETERINARIAN;
  }

  @nativeAsync
  async search() {
    markFormGroup(this.form);
    if (this.form.valid) {
      const { cpf, noCpf, dateOfBirth } = this.form.value;
      this.loadingSpinnerEvent.emit(true);

      if (!noCpf && this.typePatientSelect === this.enumTypePatient.ADULT) {
        const patientLinked = await this.patientLinked(cpf);
        if (Boolean(patientLinked)) {
          this.notification.notify(
            'warning',
            'Aviso',
            'O paciente informado já existe na sua lista de pacientes'
          );
          this.loadingSpinnerEvent.emit(false);
          this.modalRef.hide();
          return;
        }
      }

      try {
        const patient = await this.getPatientByCpf(cpf);
        const isUnderAge = DateUtils.isUnderAge(patient.dateOfBirth);
        if (this.isVeterinarian) {
          this.openModalSelectPetEvent.emit({ patient });
        } else if (
          !noCpf &&
          ((this.typePatientSelect === this.enumTypePatient.ADULT && !isUnderAge) ||
            (this.typePatientSelect === this.enumTypePatient.MINOR && isUnderAge))
        ) {
          await this.addPatientToProfessional(patient._id);
          this.updateListPatientEvent.emit(patient);
          this.notification.notify(
            'warning',
            'Aviso',
            'O Paciente informado já existe! Adicionando o mesmo na sua lista de pacientes'
          );
        } else if (isUnderAge) {
          let mensage = '';
          if (this.typePatientSelect === this.enumTypePatient.ADULT) {
            mensage = 'O CPF é de um paciente menor, tente novamente como Paciente Menor';
          } else if (this.typePatientSelect === this.enumTypePatient.MINOR && noCpf) {
            mensage = 'CPF é de um menor, tente novamente com o CPF do responsável';
          } else {
            mensage = 'O CPF é de um paciente maior, tente novamente como Paciente Maior';
          }

          this.notification.notify('warning', 'Aviso', mensage);
        } else if (this.typePatientSelect === this.enumTypePatient.MINOR && !noCpf) {
          this.notification.notify(
            'warning',
            'Aviso',
            'O CPF é de um paciente maior, tente novamente como Paciente Maior'
          );
        } else {
          this.openModalAddPatientEvent.emit({
            patient: patient,
            isResponsible: noCpf,
            existingPatient: true
          });
        }
        this.modalRef.hide();
      } catch (err) {
        if (err.status === 404) {
          this.consultCpf(cpf, noCpf, dateOfBirth);
        } else {
          this.notification.notify('error', 'Atenção', 'Ocorreu um erro ao tentar buscar paciente.');
        }
      }
    }
    this.loadingSpinnerEvent.emit(false);
  }
  @nativeAsync
  async consultCpf(cpf: string, noCpf: boolean, dateOfBirth?: string) {
    try {
      this.loadingSpinnerEvent.emit(true);
      const consult = await this.bigboostService.consultCpf(cpf, dateOfBirth).toPromise();
      this.modalRef.hide();
      this.loadingSpinnerEvent.emit(false);
      if (consult.menorIdade && this.enumTypePatient.ADULT === this.typePatientSelect) {
        this.notification.notify(
          'warning',
          'Aviso',
          'O CPF é de um paciente menor, tente novamente como Paciente Menor'
        );
      } else if (
        !consult.menorIdade &&
        !noCpf &&
        this.enumTypePatient.MINOR === this.typePatientSelect
      ) {
        this.notification.notify(
          'warning',
          'Aviso',
          ' O CPF é de um paciente maior, tente novamente como Paciente Maior'
        );
      } else if (consult.menorIdade && noCpf) {
        this.notification.notify(
          'warning',
          'Aviso',
          'CPF é de um menor, tente novamente com o CPF do responsável'
        );
      } else {
        this.openModalAddPatientEvent.emit({
          patient: consult,
          isResponsible: noCpf || this.isVeterinarian,
          existingPatient: false
        });
      }
    } catch (error) {
      if (error.status === 401) {
        this.notification.notify(
          'warning',
          'Aviso',
          'A data de nascimento não pertence a este CPF. Favor digitar novamente.'
        );
        this.loadingSpinnerEvent.emit(false);
        this.form.get('dateOfBirth').setErrors({ nonMatchingCpf: true });
        return;
      }
      if (error.status === 404) {
        this.modalRef.hide();
        this.notification.notify(
          'error',
          'Error ao consultar CPF',
          'CPF não localizado na Receita Federal. Verifique as informações digitadas e tente novamente. Em caso de dúvidas, entre em contato com nosso atendimento',
          8000
        );
        this.loadingSpinnerEvent.emit(false);

        return;
      }
      this.notification.notify(
        'error',
        'Atenção',
        'A importação de dados do CPF está com instabilidade, preencha manualmente ou tente novamente mais tarde.'
      );
      this.openModalAddPatientEvent.emit({
        patient: null,
        isResponsible: noCpf,
        existingPatient: false
      });
    }
  }

  tratarPacienteAdulto(patient: Patient) {
    const isUnderAge = DateUtils.isUnderAge(patient.dateOfBirth);
    if (isUnderAge) {
    } else {
    }
  }
  @nativeAsync
  async patientLinked(cpf: string) {
    const result = await this.healthProfessionalService
      .getPatientsByUserId(cpf, this.user._id)
      .toPromise();

    return result.length ? result[0] : null;
  }

  changeTypePatient(typePatient: TypePatientEnum) {
    this.typePatientSelect = typePatient;
    if (this.typePatientSelect === this.enumTypePatient.MINOR) {
      this.form.get('dateOfBirth').setValidators([Validators.required]);
    }
  }
  @nativeAsync
  private async addPatientToProfessional(patientId: string) {
    await this.healthProfessionalService.addPatient(this.prescriber._id, patientId).toPromise();
  }
  @nativeAsync
  private async getPatientByCpf(cpf: string) {
    return await this.patientsService.getPatientByCpf(cpf).toPromise();
  }
}
